<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import Dialog from 'primevue/dialog';
import DatePicker from 'primevue/datepicker';
import Select from 'primevue/select';
import Textarea from 'primevue/textarea';
import { CustomButton, ButtonType } from '@/components/common/';
import { useCaseNoteStore } from '@/stores/caseNote';
import { useStudyStore } from '@/stores/study';
import { type CaseNoteModel, type TimepointOption, type PostCaseNote } from '@/models/caseNote';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  caseIdentifier: {
    type: String,
    required: true
  },
  existingNote: {
    type: Object as PropType<CaseNoteModel>,
    default: undefined
  },
  timepointOptions: {
    type: Array as PropType<TimepointOption[]>,
    default: () => []
  }
});

const emit = defineEmits(['onClose', 'onSaved']);

const caseNoteStore = useCaseNoteStore();
const studyStore = useStudyStore();

type NoteMode = 'event' | 'timepoint';

const noteMode = ref<NoteMode>('event');
const eventTime = ref<Date | null>(null);
const maxEventDate = ref(new Date());
const selectedTimepoint = ref<TimepointOption | undefined>();
const noteContent = ref('');

const isPumpStudy = computed(() => studyStore.selectedStudy?.flags?.customDataDictionary === 'PUMP');
const availableTimepoints = computed(() => props.timepointOptions);

const resetForm = () => {
  noteMode.value = 'event';
  eventTime.value = new Date();
  selectedTimepoint.value = undefined;
  noteContent.value = '';
};

watch(
  () => props.visible,
  isVisible => {
    if (!isVisible) return;
    maxEventDate.value = new Date();

    if (props.existingNote) {
      noteContent.value = props.existingNote.content;

      if (props.existingNote.eventTime) {
        noteMode.value = 'event';
        eventTime.value = props.existingNote.eventTime;
      } else if (props.existingNote.timepoint) {
        noteMode.value = 'timepoint';
        selectedTimepoint.value =
          availableTimepoints.value.find(t => t.value === props.existingNote!.timepoint) ??
          { label: props.existingNote.timepoint, value: props.existingNote.timepoint };
      } else {
        noteMode.value = 'event';
        eventTime.value = new Date();
        selectedTimepoint.value = undefined;
      }
    } else {
      resetForm();
    }
  }
);

const isEditing = () => !!props.existingNote;

const isFormValid = () => {
  if (!noteContent.value.trim()) return false;
  if (noteMode.value === 'event') return !!eventTime.value;
  // timepoint mode — only reachable for pump studies
  return !!selectedTimepoint.value;
};

const handleSave = async () => {
  if (!isFormValid()) return;

  const payload: PostCaseNote = {
    content: noteContent.value.trim()
  };

  if (noteMode.value === 'event') {
    payload.eventTime = eventTime.value!.toISOString();
  } else {
    payload.timepoint = selectedTimepoint.value!.value;
  }

  const savedNote = isEditing() ?
    await caseNoteStore.updateNote(props.caseIdentifier, props.existingNote!.id, payload) :
    await caseNoteStore.createNote(props.caseIdentifier, payload);

  if (!savedNote) {
    return;
  }

  emit('onSaved');
  emit('onClose');
};

const handleClose = () => {
  emit('onClose');
};

const setMode = (mode: NoteMode) => {
  noteMode.value = mode;
  if (mode === 'event' && !eventTime.value) {
    eventTime.value = new Date();
  }
};
</script>

<template>
  <Dialog :visible="visible"
    modal
    :header="isEditing() ? 'Edit Note' : 'Add Note'"
    class="note-dialog"
    style="width: 480px;"
    @update:visible="handleClose">
    <div class="note-form">
      <!-- Mode toggle — pump study only -->
      <div v-if="isPumpStudy" class="mode-toggle">
        <button type="button"
          class="toggle-btn"
          :class="{ active: noteMode === 'event' }"
          @click="setMode('event')">
          Event time
        </button>
        <button type="button"
          class="toggle-btn"
          :class="{ active: noteMode === 'timepoint' }"
          @click="setMode('timepoint')">
          Timepoint
        </button>
      </div>

      <!-- Event time picker (default / non-pump always) -->
      <template v-if="noteMode === 'event'">
        <label class="field-label">Event time</label>
        <DatePicker v-model="eventTime"
          showTime
          hourFormat="24"
          :maxDate="maxEventDate"
          showIcon
          iconDisplay="input"
          fluid
          placeholder="Select date and time"
          class="event-time-picker">
          <template #inputicon="slotProps">
            <i class="pi pi-calendar-clock" @click="slotProps.clickCallback"/>
          </template>
        </DatePicker>
      </template>

      <!-- Timepoint picker — pump study only -->
      <template v-if="isPumpStudy && noteMode === 'timepoint'">
        <label class="field-label">Timepoint</label>
        <Select v-model="selectedTimepoint"
          :options="availableTimepoints"
          optionLabel="label"
          placeholder="Select a timepoint"
          class="timepoint-select"/>
      </template>

      <label class="field-label">Note</label>
      <Textarea v-model="noteContent"
        placeholder="Enter your note here..."
        rows="5"
        autoResize
        class="note-textarea"/>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <CustomButton text="Cancel"
          :type="ButtonType.OUTLINED"
          @onClick="handleClose"/>
        <CustomButton :text="isEditing() ? 'Save Changes' : 'Add Note'"
          :type="ButtonType.POSITIVE"
          :margin-left="12"
          :disabled="!isFormValid()"
          :loading="caseNoteStore.noteActionLoader.isLoading"
          @onClick="handleSave"/>
      </div>
    </template>
  </Dialog>
</template>

<style scoped lang="scss">
@import '../../../assets/_dialog.scss';

.note-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0 16px;
}

.mode-toggle {
  display: flex;
  gap: 0;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 4px;
  width: fit-content;
}

.toggle-btn {
  padding: 6px 18px;
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--color-text);
  transition: background 0.15s ease, color 0.15s ease;

  &:first-child {
    border-right: 1px solid var(--color-border);
  }

  &.active {
    background: var(--p-primary-color, #0066ff);
    color: #fff;
  }

  &:not(.active):hover {
    background: var(--color-background-mute);
  }
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-heading);
  margin-top: 8px;

  &:first-child {
    margin-top: 0;
  }
}

.event-time-picker {
  width: 100%;
}

.timepoint-select {
  width: 100%;
}

.note-textarea {
  width: 100%;
  resize: vertical;
  font-family: inherit;
  font-size: 14px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
}
</style>
